import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Gauge, ArrowRight, Loader2 } from "lucide-react";

const BAND_TONE = {
  Outstanding: "text-accent", Good: "text-emerald-600", "Requires Improvement": "text-amber-500", Inadequate: "text-destructive",
};
const RING = { Outstanding: "#059669", Good: "#10b981", "Requires Improvement": "#f59e0b", Inadequate: "#ef4444" };

export default function ReadinessScore({ refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/readiness").then((r) => setData(r.data)).catch(() => {}); }, [refreshKey]);

  if (!data) return <div className="bg-card border border-border rounded-2xl p-6 shadow-sm flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const r = 52, c = 2 * Math.PI * r, dash = (data.score / 100) * c;

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm p-6 grid md:grid-cols-5 gap-6" data-testid="readiness-card">
      <div className="md:col-span-2 flex flex-col items-center justify-center text-center">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5 mb-3"><Gauge className="w-4 h-4" /> Inspection readiness</span>
        <div className="relative w-36 h-36">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r={r} fill="none" stroke="currentColor" className="text-secondary" strokeWidth="10" />
            <circle cx="60" cy="60" r={r} fill="none" stroke={RING[data.band]} strokeWidth="10" strokeLinecap="round"
              strokeDasharray={`${dash} ${c - dash}`} style={{ transition: "stroke-dasharray 0.8s ease" }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-display text-4xl font-extrabold leading-none" data-testid="readiness-score">{data.score}</span>
            <span className="text-[10px] text-muted-foreground">out of 100</span>
          </div>
        </div>
        <Badge variant="secondary" className={`mt-3 border-0 bg-secondary ${BAND_TONE[data.band]}`} data-testid="readiness-band">{data.band}</Badge>
        <p className="text-[11px] text-muted-foreground mt-2">{data.regulator} · blends checklist, interviews and policy coverage</p>
      </div>
      <div className="md:col-span-3 space-y-4">
        <div className="space-y-2.5">
          {data.breakdown.map((b) => (
            <div key={b.id} data-testid={`readiness-${b.id}`}>
              <div className="flex justify-between text-sm mb-1">
                <span>{b.label} <span className="text-xs text-muted-foreground">· {b.weight}%</span></span>
                <span className="font-semibold">{b.score}</span>
              </div>
              <Progress value={b.score} className="h-1.5" />
              <p className="text-[11px] text-muted-foreground mt-0.5">{b.detail}</p>
            </div>
          ))}
        </div>
        {data.actions.length > 0 && (
          <div className="border-t border-border pt-3">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Raise your score</p>
            <ul className="space-y-1.5">
              {data.actions.map((a, i) => (
                <li key={a.link || a.label}>
                  <Link to={a.link} className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid={`readiness-action-${i}`}>
                    {a.label} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
